import clsx from 'clsx';
import { Fragment, useState } from 'react';
import { ChevronDown, ChevronRight, Camera, EyeOff, MousePointer2, Trash2 } from 'lucide-react';
import { DateTime } from 'luxon';
import { CLASS_LABELS, type FlaggedRangeDto, type ProductivityDayDto, type ScreenshotDto } from '@workforce/shared';
import { formatMinutes, t } from '@/lib/i18n';
import { useTz } from '@/lib/timezone';
import { useAuth } from '@/lib/auth';
import { Badge, Button, Modal } from './ui';

export const CLASS_COLORS: Record<string, string> = {
  productive: 'bg-cls-productive',
  unproductive: 'bg-cls-unproductive',
  neutral: 'bg-cls-neutral',
  unknown: 'bg-cls-unknown',
  idle: 'bg-cls-idle',
};
const TONE: Record<string, 'green' | 'red' | 'slate' | 'amber'> = { productive: 'green', unproductive: 'red', neutral: 'slate', unknown: 'amber' };

export function ClassBadge({ c }: { c: string }) {
  return <Badge tone={TONE[c] ?? 'slate'}>{CLASS_LABELS[c as keyof typeof CLASS_LABELS] ? t(CLASS_LABELS[c as keyof typeof CLASS_LABELS]) : c}</Badge>;
}

/** Stacked bar of one day: productive / neutral / not classified / unproductive / idle. */
export function DayBar({ d, className }: { d: ProductivityDayDto; className?: string }) {
  const parts: [string, number][] = [
    ['productive', d.productiveMinutes],
    ['neutral', d.neutralMinutes],
    ['unknown', d.unknownMinutes],
    ['unproductive', d.unproductiveMinutes],
    ['idle', d.idleMinutes],
  ];
  const total = parts.reduce((a, [, m]) => a + m, 0);
  if (!total) return <div className={clsx('h-2.5 rounded-full bg-slate-100', className)} />;
  return (
    <div className={clsx('flex h-2.5 overflow-hidden rounded-full bg-slate-100', className)} role="img" aria-label={parts.map(([k, m]) => `${t(CLASS_LABELS[k as keyof typeof CLASS_LABELS])} ${formatMinutes(m)}`).join(t(', '))}>
      {parts.map(([k, m]) => (m ? <div key={k} className={CLASS_COLORS[k]} style={{ width: `${(m / total) * 100}%` }} title={`${t(CLASS_LABELS[k as keyof typeof CLASS_LABELS])}: ${formatMinutes(m)}`} /> : null))}
    </div>
  );
}

export function Legend() {
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
      {(['productive', 'neutral', 'unknown', 'unproductive', 'idle'] as const).map((k) => (
        <span key={k} className="inline-flex items-center gap-1.5">
          <span className={clsx('h-2.5 w-2.5 rounded-full', CLASS_COLORS[k])} />
          {t(CLASS_LABELS[k])}
        </span>
      ))}
    </div>
  );
}

/**
 * Screenshots gallery. `imageBase` is the API path without the trailing id, e.g.
 * "/api/productivity/screenshots" (admin) or "/api/me/productivity/screenshots" (self).
 * `autoThumbs` shows the pictures directly (the employee's own, not audited); when false
 * each tile is a placeholder and the picture loads only when opened — so an administrator's
 * every view is a single, deliberate, audited action.
 */
export function ScreenshotGallery({
  screenshots,
  imageBase,
  timezone,
  autoThumbs,
  onDelete,
}: {
  screenshots: ScreenshotDto[];
  imageBase: string;
  timezone?: string | null;
  autoThumbs?: boolean;
  onDelete?: (s: ScreenshotDto) => void;
}) {
  const { fmt } = useTz();
  const [open, setOpen] = useState<ScreenshotDto | null>(null);
  if (!screenshots.length) return <p className="px-1 py-2 text-sm text-slate-500">{t('No screenshots in this period.')}</p>;
  const url = (s: ScreenshotDto) => `${imageBase}/${s.id}/image`;
  return (
    <>
      <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 md:grid-cols-6">
        {screenshots.map((s) => {
          const time = fmt(s.capturedAt, timezone ?? null, 'dd LLL, HH:mm');
          if (s.deleted) {
            return (
              <div key={s.id} className="flex aspect-video flex-col items-center justify-center gap-1 rounded-lg border border-dashed border-slate-200 bg-slate-50 text-center">
                <EyeOff className="h-4 w-4 text-slate-400" />
                <span className="px-1 text-[10px] leading-tight text-slate-400">{t('Removed')}</span>
                <span className="text-[10px] tabular-nums text-slate-400">{time}</span>
              </div>
            );
          }
          return (
            <button
              key={s.id}
              type="button"
              onClick={() => setOpen(s)}
              className="group relative aspect-video overflow-hidden rounded-lg border border-slate-200 bg-slate-100 text-start hover:border-brand-400 hover:ring-2 hover:ring-brand-200"
              title={time}
            >
              {autoThumbs ? (
                <img src={url(s)} alt={t('Screenshot from {time}', { time })} loading="lazy" className="h-full w-full object-cover" />
              ) : (
                <span className="flex h-full w-full items-center justify-center text-slate-300 group-hover:text-brand-400"><Camera className="h-5 w-5" /></span>
              )}
              <span className="absolute inset-x-0 bottom-0 flex items-center justify-between gap-1 bg-black/55 px-1.5 py-0.5 text-[10px] font-medium text-white">
                <span className="tabular-nums">{fmt(s.capturedAt, timezone ?? null, 'HH:mm')}</span>
                {s.blurred && <span className="rounded bg-white/25 px-1">{t('blurred')}</span>}
              </span>
            </button>
          );
        })}
      </div>
      <Modal
        open={!!open}
        onClose={() => setOpen(null)}
        title={open ? t('Screenshot — {time}', { time: fmt(open.capturedAt, timezone ?? null, 'dd LLL yyyy, HH:mm') }) : ''}
        footer={
          <>
            {open && onDelete && (
              <Button variant="danger" icon={<Trash2 className="h-4 w-4" />} onClick={() => { onDelete(open); setOpen(null); }}>
                {t('Delete this screenshot')}
              </Button>
            )}
            <Button variant="secondary" onClick={() => setOpen(null)}>{t('Close')}</Button>
          </>
        }
      >
        {open && (
          <div className="space-y-2">
            <img src={url(open)} alt={t('Screenshot from {time}', { time: fmt(open.capturedAt, timezone ?? null, 'dd LLL, HH:mm') })} className="max-h-[70vh] w-full rounded-lg border border-slate-200 object-contain" />
            {onDelete && <p className="text-xs text-slate-500">{t('Deleting removes this screenshot for everyone and cannot be undone. Recorded working time is not changed.')}</p>}
          </div>
        )}
      </Modal>
    </>
  );
}

/** Stretches flagged for review (mouse jiggler / mouse-only). Shown to managers, never auto-deducted. */
export function FlaggedList({ flagged, timezone }: { flagged: FlaggedRangeDto[]; timezone?: string | null }) {
  const { fmt } = useTz();
  if (!flagged.length) return null;
  const reason = (r: FlaggedRangeDto['reason']) => (r === 'injected' ? t('Regular, machine-like mouse movement (possible mouse jiggler)') : t('Mouse movement only, no typing or clicking'));
  return (
    <div className="space-y-2">
      <p className="flex items-center gap-1.5 text-sm font-medium text-amber-700"><MousePointer2 className="h-4 w-4" /> {t('Flagged for your review')}</p>
      <ul className="space-y-1 text-sm">
        {flagged.map((f, i) => (
          <li key={i} className="flex flex-wrap items-center gap-x-2 gap-y-0.5 rounded-lg bg-amber-50 px-3 py-2 text-amber-900">
            <span className="tabular-nums font-medium">{fmt(f.from, timezone ?? null, 'dd LLL, HH:mm')} – {fmt(f.to, timezone ?? null, 'HH:mm')}</span>
            <span className="text-amber-700">· {formatMinutes(f.minutes)}</span>
            <span className="w-full text-xs text-amber-700/90 sm:w-auto">· {reason(f.reason)}</span>
          </li>
        ))}
      </ul>
      <p className="text-xs text-slate-500">{t('These are only pointers for a human to check — they are never deducted from pay automatically.')}</p>
    </div>
  );
}

/** Score colour: at/above target green, up to 20 points below amber, lower red. */
export const pctTone = (p: number | null, target = 70) =>
  p == null ? 'text-slate-400' : p >= target ? 'text-emerald-700' : p >= target - 20 ? 'text-amber-700' : 'text-red-600';

/** One row per day (or per person with `showEmployee`), expandable to the apps and websites used. */
export function ProductivityTable({ days, showEmployee, onEmployee }: { days: ProductivityDayDto[]; showEmployee?: boolean; onEmployee?: (id: string) => void }) {
  const { fmt } = useTz();
  const target = useAuth().user?.productivityTargetPct ?? 70;
  const [open, setOpen] = useState<Record<string, boolean>>({});
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead>
          <tr className="border-b border-slate-200 text-start text-xs font-medium uppercase tracking-wide text-slate-500">
            <th className="w-8 px-2 py-2" />
            <th className="px-3 py-2">{showEmployee ? t('Employee') : t('Date')}</th>
            <th className="hidden px-3 py-2 md:table-cell">{t('From – to')}</th>
            <th className="min-w-40 px-3 py-2">{t('Day')}</th>
            <th className="hidden px-3 py-2 text-end sm:table-cell">{t('Tracked')}</th>
            <th className="hidden px-3 py-2 text-end md:table-cell">{t('Productive')}</th>
            <th className="hidden px-3 py-2 text-end lg:table-cell">{t('Unproductive')}</th>
            <th className="hidden px-3 py-2 text-end lg:table-cell">{t('Idle')}</th>
            <th className="px-3 py-2 text-end">{t('Score')}</th>
          </tr>
        </thead>
        <tbody>
          {days.map((d) => {
            const k = `${d.employeeId}:${d.date}`;
            const empty = d.trackedMinutes === 0;
            return (
              <Fragment key={k}>
                <tr className={clsx('border-b border-slate-100', !empty && 'cursor-pointer hover:bg-slate-50')} onClick={() => !empty && setOpen((o) => ({ ...o, [k]: !o[k] }))}>
                  <td className="px-2 py-2 text-slate-400">{!empty && (open[k] ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />)}</td>
                  <td className="whitespace-nowrap px-3 py-2 font-medium">
                    {showEmployee ? (
                      onEmployee ? (
                        <button className="text-brand-700 hover:underline" onClick={(e) => { e.stopPropagation(); onEmployee(d.employeeId); }}>
                          {d.employeeName}
                        </button>
                      ) : (
                        d.employeeName
                      )
                    ) : (
                      DateTime.fromISO(d.date).toFormat('ccc dd LLL')
                    )}
                  </td>
                  <td className="hidden whitespace-nowrap px-3 py-2 tabular-nums text-slate-500 md:table-cell">
                    {d.firstActivity ? `${fmt(d.firstActivity, d.timezone, 'HH:mm')} – ${fmt(d.lastActivity, d.timezone, 'HH:mm')}` : '—'}
                  </td>
                  <td className="px-3 py-2"><DayBar d={d} /></td>
                  <td className="hidden whitespace-nowrap px-3 py-2 text-end tabular-nums sm:table-cell">{empty ? '—' : formatMinutes(d.trackedMinutes)}</td>
                  <td className="hidden whitespace-nowrap px-3 py-2 text-end tabular-nums text-emerald-700 md:table-cell">{empty ? '—' : formatMinutes(d.productiveMinutes)}</td>
                  <td className="hidden whitespace-nowrap px-3 py-2 text-end tabular-nums text-red-600 lg:table-cell">{empty ? '—' : formatMinutes(d.unproductiveMinutes)}</td>
                  <td className="hidden whitespace-nowrap px-3 py-2 text-end tabular-nums text-slate-500 lg:table-cell">{empty ? '—' : formatMinutes(d.idleMinutes)}</td>
                  <td className={clsx('whitespace-nowrap px-3 py-2 text-end font-semibold tabular-nums', pctTone(d.productivityPct, target))}>{d.productivityPct == null ? '—' : `${d.productivityPct}%`}</td>
                </tr>
                {open[k] && (
                  <tr className="border-b border-slate-100 bg-slate-50/60">
                    <td />
                    <td colSpan={8} className="px-3 py-3">
                      <div className="grid gap-x-8 gap-y-1 sm:grid-cols-2">
                        {d.apps.map((a) => (
                          <div key={`${a.kind}:${a.key}`} className="flex items-center gap-2 text-sm">
                            <span className={clsx('h-2 w-2 shrink-0 rounded-full', CLASS_COLORS[a.classification])} />
                            <span className="min-w-0 flex-1 truncate" title={a.key}>
                              {a.label}
                              {a.kind === 'domain' && a.label !== a.key ? <span className="text-slate-400"> · {a.key}</span> : null}
                            </span>
                            <span className="tabular-nums text-slate-600">{formatMinutes(a.minutes)}</span>
                          </div>
                        ))}
                        {!d.apps.length && <p className="text-sm text-slate-500">{t('Only idle time.')}</p>}
                      </div>
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
